import { forwardRef } from "react";
import {
  useTurnIntoDropdown,
  getFilteredBlockTypeOptions,
} from "@/tiptap/ui/turn-into-dropdown"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Tiptap UI Components ---
import { TextButton } from "@/tiptap/ui/text-button"
import { HeadingButton } from "@/tiptap/ui/heading-button"
import { ListButton } from "@/tiptap/ui/list-button"
import { BlockquoteButton } from "@/tiptap/ui/blockquote-button"
import { CodeBlockButton } from "@/tiptap/ui/code-block-button"
import { buildCalloutAction } from "@/tiptap/ui/callout-menu/callout-menu"
import { turnActiveCalloutListIntoList } from "@/tiptap/ui/turn-into-dropdown/callout-list-transform"
import { ChevronRightIcon } from "@/tiptap/icons/chevron-right-icon"
import { Spacer } from "@/tiptap/ui-primitive/spacer"

import { Button } from "@/tiptap/ui-primitive/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/tiptap/ui-primitive/dropdown-menu"

export function TurnIntoDropdownContent({
  blockTypes,
  editor: providedEditor
}) {
  const { editor } = useTiptapEditor(providedEditor)
  const filteredOptions = getFilteredBlockTypeOptions(blockTypes, editor)

  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>Turn into</DropdownMenuLabel>
      {filteredOptions.map((option, index) =>
        renderBlockTypeButton(option, `${option.type}-${option.level ?? index}`, editor))}
    </DropdownMenuGroup>
  );
}

function renderBlockTypeButton(
  option,
  key,
  editor
) {
  const handleListTurnIntoClick = (event) => {
    if (turnActiveCalloutListIntoList(editor, option.type)) {
      event.preventDefault()
    }
  }

  switch (option.type) {
    case "paragraph":
      return (
        <DropdownMenuItem key={key} asChild>
          <TextButton editor={editor} showTooltip={false} text={option.label} />
        </DropdownMenuItem>
      );

    case "heading":
      if (!option.level) return null
      return (
        <DropdownMenuItem key={key} asChild>
          <HeadingButton
            editor={editor}
            level={option.level}
            showTooltip={false}
            text={option.label} />
        </DropdownMenuItem>
      );

    case "bulletList":
      return (
        <DropdownMenuItem key={key} asChild>
          <ListButton
            editor={editor}
            type="bulletList"
            showTooltip={false}
            text={option.label}
            onClick={handleListTurnIntoClick} />
        </DropdownMenuItem>
      );

    case "orderedList":
      return (
        <DropdownMenuItem key={key} asChild>
          <ListButton
            editor={editor}
            type="orderedList"
            showTooltip={false}
            text={option.label}
            onClick={handleListTurnIntoClick} />
        </DropdownMenuItem>
      );

    case "taskList":
      return (
        <DropdownMenuItem key={key} asChild>
          <ListButton
            editor={editor}
            type="taskList"
            showTooltip={false}
            text={option.label}
            onClick={handleListTurnIntoClick} />
        </DropdownMenuItem>
      );

    case "blockquote":
      return (
        <DropdownMenuItem key={key} asChild>
          <BlockquoteButton editor={editor} showTooltip={false} text={option.label} />
        </DropdownMenuItem>
      );

    case "callout":
      const calloutAction = buildCalloutAction(editor)
      const CalloutMenuIcon = calloutAction.icon

      return (
        <DropdownMenuSub key={key}>
          <DropdownMenuSubTrigger asChild className="tiptap-turn-into-callout-trigger">
            <Button variant="ghost" showTooltip={false}>
              <CalloutMenuIcon className="tiptap-button-icon" />
              <span className="tiptap-button-text">{option.label}</span>
              <Spacer />
              <ChevronRightIcon className="tiptap-button-icon" />
            </Button>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="tiptap-turn-into-callout-sub-content">
              <DropdownMenuGroup>
                <DropdownMenuLabel>Callout</DropdownMenuLabel>
                {calloutAction.items.map((item) => {
                  const VariantIcon = item.icon

                  return (
                    <DropdownMenuItem
                      className="tiptap-turn-into-callout-sub-item"
                      key={`${key}-${item.variant}`}
                      data-callout-variant={item.calloutVariant}
                      data-active-state={item.isActive ? "on" : "off"}
                      onSelect={item.onSelect}>
                      <Button
                        type="button"
                        variant="ghost"
                        showTooltip={false}
                        data-callout-variant={item.calloutVariant}
                        data-active-state={item.isActive ? "on" : "off"}>
                        <VariantIcon className="tiptap-button-icon" />
                        <span className="tiptap-button-text">{item.label}</span>
                      </Button>
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      );

    case "codeBlock":
      return (
        <DropdownMenuItem key={key} asChild>
          <CodeBlockButton editor={editor} showTooltip={false} text={option.label} />
        </DropdownMenuItem>
      );

    default:
      return null
  }
}

/**
 * Dropdown component for transforming block types in a Tiptap editor.
 * For custom dropdown implementations, use the `useTurnIntoDropdown` hook instead.
 */
export const TurnIntoDropdown = forwardRef(function TurnIntoDropdown(
  {
    editor: providedEditor,
    hideWhenUnavailable = false,
    blockTypes,
    onOpenChange,
    modal = true,
    children,
    ...buttonProps
  },
  ref
) {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    canToggle,
    isOpen,
    activeBlockType,
    handleOpenChange,
    label,
    Icon,
  } = useTurnIntoDropdown({
    editor,
    hideWhenUnavailable,
    blockTypes,
    onOpenChange,
  })

  if (!isVisible) return null

  return (
    <DropdownMenu modal={modal} open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          disabled={!canToggle}
          data-disabled={!canToggle}
          role="button"
          tabIndex={-1}
          aria-label={label}
          tooltip="Turn into"
          {...buttonProps}
          ref={ref}>
          {children ?? (
            <>
              <span className="tiptap-button-text">
                {activeBlockType?.label || "Text"}
              </span>
              <Icon className="tiptap-button-dropdown-small" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <TurnIntoDropdownContent blockTypes={blockTypes} editor={editor} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
})

TurnIntoDropdown.displayName = "TurnIntoDropdown"
